import {faAdd, faBell, faBuildings, faClock, faPowerOff, faUserGroup} from '@fortawesome/pro-light-svg-icons'
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { ConnectedProps, connect } from 'react-redux'
import { Link } from 'react-router-dom'
import useTwinTranslation from '../../../../../utils/hooks/useTwinTranslation'
import { AllReduxAppPayloads, GlobalContext } from '../../../../../utils/reducers'
import { LastNotificationPayload } from '../../../../../utils/reducers/company/lastNotification'
import { getBottomNavApp, getConfigParam, getUserInfo, getUserInfoSubType, getUserPermissions } from '../../../../../utils/reducers/getters'
import { UserPayload } from '../../../../../utils/reducers/globals/user'
import { changeMultiUser, signOutUser } from '../../../../../utils/reducers/reduxDispatch'
import { checkIfAnimated } from '../../../../Modal/functions'
import { ModalOpenedTy } from '../../../../Modal/types'
import { PopUpMessage } from '../../../../ModalsLayouts/ModalPopupMessage'
import PermissionChecker, { ModuleChecker } from '../../../../PermissionChecker'
import TwinIcon from '../../../../TwinIcon'
import ModalSelectorCompanyManager from './ModalSelectorCompanyManager'
import './user_alerts.sass'
import useEditDeleteModal from '../../../../../utils/hooks/useEditDeleteModal'
import ModalCreateTaskWithHelper from '../../../../../specificComponents/Task/ModalCreateTaskWithHelper'
import { Routes } from '../../../../CreateRoutering/types'
import Shortcuts from './ShortcutIcons'
import ModalWorkingAntiLogout from '../../../../ModalsLayouts/ModalWorkingAntiLogout'
import { TwinDictionary } from '../../../../../utils/globals/dictionary'
import { useInactivity } from '../../../../../utils/hooks/useInactivity'

interface UserIconsProps {
    routes: Routes
    onClick: () => void
}

const UserIcons: React.FC<UserIconsProps> = ({routes, onClick}) => {
    if(!routes || routes.length === 0){
        return <div className='flex flex-col justify-end items-center mt-auto user_alerts w-full gap-y-24'>
            <SignOut />
        </div>
    }
    return (
        <div className='flex flex-col justify-end items-center mt-auto user_alerts w-full gap-y-24'>
            <Shortcuts/>
            <User onClick={onClick} />
            <CreateTask />
            <SelectorCompanyConnected />
            <TimeControl onClick={onClick} />
            <NotificationsConnect onClick={onClick} />
            <SignOut />
        </div>
    )
}

interface CreateTaskProps { }

export const CreateTask: React.FC<CreateTaskProps> = () => {
    const { openModal, setOpenModal } = useCreateTaskLogic()
    const userPermissions = getUserPermissions('task.main')
    return (
        <PermissionChecker userPermissions={userPermissions} permission={'create'}>
            <ModuleChecker modules={['tasks']}>
                <TwinIcon icon={faAdd} className='user_alerts_icons ' onClick={() => setOpenModal({type: 'edit', allRowData: {}})} />
                {openModal?.type === 'edit' ? <ModalCreateTaskWithHelper onSubmit={() => { }} setOpened={() => setOpenModal(null)} allRowData={openModal?.allRowData} userPermissions={userPermissions || undefined} /> : null}
            </ModuleChecker>
        </PermissionChecker>

    )
}

const useCreateTaskLogic = () => {
    const { openModal, setOpenModal } = useEditDeleteModal()

    return { openModal, setOpenModal }
}

interface UserProps {
    onClick: () => void
}

const User: React.FC<UserProps> = ({ onClick }) => {
    const { user } = useUserLogic()
    const profileImage = user?.Employee?.profile_image || '/user-png.png'
    return (
        <Link onClick={onClick} to={'/employeeProfile/main'}>
            <div className='logo_user cursor-pointer'>
                <img src={profileImage} className='rounded-full object-cover h-full w-full' alt='logo user' />
            </div>
        </Link>

    )
}

const useUserLogic = () => {
    const user = getUserInfo()
    return { user }
}


interface SelectorCompanyProps extends ReduxSelectorCompanyConnector { }

const SelectorCompany: React.FC<SelectorCompanyProps> = ({ user }) => {
    const { opened, setOpened } = useSelectorCompanyLogic()
    if (!user?.extraData.isManager) {
        return null
    }
    return (
        <div>
            <TwinIcon icon={faBuildings} className='user_alerts_icons ' onClick={() => setOpened(true)} />
            {opened ? <ModalSelectorCompanyManager {...{ opened, setOpened }} /> : null}
        </div>
    )
}

const useSelectorCompanyLogic = () => {
    const [opened, setOpened] = useState<ModalOpenedTy>(null)
    return { opened, setOpened }
}

const selectorCompanyDispatch = {
    setUser: (payload: UserPayload) => ({ type: 'CHANGE_USER', payload }),
}
const mapSelectorCompanyConnector = (state: AllReduxAppPayloads) => ({ user: state.user })
const selectorCompanyConnector = connect(mapSelectorCompanyConnector, selectorCompanyDispatch, null, { context: GlobalContext })
type ReduxSelectorCompanyConnector = ConnectedProps<typeof selectorCompanyConnector>
export const SelectorCompanyConnected = selectorCompanyConnector(SelectorCompany)

interface TimeControlProps {
    onClick: () => void
}

export const TimeControl: React.FC<TimeControlProps> = ({ onClick }) => {
    return (
        <ModuleChecker modules={['working_calendar', 'working_calendar_medium', 'working_calendar_full']}>
            <Link onClick={onClick} to={'/timeControl/calendar'}>
                <div className='flex flex-col items-center'>
                    <TwinIcon icon={faClock} className='user_alerts_icons' />
                </div>
            </Link>
        </ModuleChecker>
    )
}

interface ChangeUserProps {}

export const ChangeUser: React.FC<ChangeUserProps> = () => {
    const userType = getUserInfoSubType()
    const timeInactivityToCloseSession = useMemo(() => parseInt(String(getConfigParam('timeInactivityToCloseSession') || 0)) || (5*60), [])
    useInactivity((timeInactivityToCloseSession || 0) * 1000, changeMultiUser, userType === 'temporal');
    if(userType !== 'temporal'){
        return null
    }
    return (
        <div className='flex flex-col items-center' onClick={() => changeMultiUser(true)}>
            <TwinIcon icon={faUserGroup} className='user_alerts_icons' />
        </div>
    )
}

interface NotificationsProps extends NotificationsLogicProps {
    onClick: () => void
}

const Notifications: React.FC<NotificationsProps> = ({ lastNotification, onClick, ...rest }) => {
    const {t} = useTwinTranslation()
    const { displayModal } = useNotificationsLogic({ lastNotification, ...rest })
    return (
        <Fragment>
            <Link onClick={onClick} to={'/notifications/all'}>
                <div className='flex flex-col items-center notifications'>
                    {lastNotification ?
                        <div className='notifications_item mb-6 rounded-full items-center' />
                        : null}
                    <TwinIcon icon={faBell} className='user_alerts_icons' />
                </div>
            </Link>
            {displayModal ? <PopUpMessage type={'success'} message={t('haveNewNotification', 'Tienes una nueva notificación')} animatedClass={checkIfAnimated(displayModal)} /> : null}
        </Fragment>
    )
}

interface NotificationsLogicProps extends ReduxLastNotification {}

const useNotificationsLogic = ({lastNotification}: NotificationsLogicProps) => {
    const [displayModal, setDisplayModal] = useState(false)

    useEffect(() => {
        if (lastNotification?.id) {
            setDisplayModal(true)
            setTimeout(() => {
                setDisplayModal(false)
            }, 3000)
        }
    }, [lastNotification?.id])

    return {displayModal}
}


const lastNotificationDispatch = {
    setLastNotification: (payload: LastNotificationPayload) => ({ type: 'CHANGE_LAST_NOTIFICATION', payload }),
}

export type ReduxLastNotification = ConnectedProps<typeof lastNotificationConnect>
const mapLastNotificationConnector = (state: AllReduxAppPayloads) => ({ lastNotification: state.lastNotification })

const lastNotificationConnect = connect(mapLastNotificationConnector, lastNotificationDispatch, null, { context: GlobalContext })

export const NotificationsConnect = lastNotificationConnect(Notifications)


interface SignOutProps {
    text?: string
 }

export const SignOut: React.FC<SignOutProps> = ({text}) => {
    const { modalBlockSignOut, setModalBlockSignOut, checkSignOut } = useSignOutLogic()
    return (
        <div>
            <Link to={'/'} onClick={checkSignOut} className='sign_out_btn'>
                <TwinIcon icon={faPowerOff} className='user_alerts_icons' />
                {text ? <div className='flex items-center sign_out_text'>{text}</div> : null}
            </Link>
            {modalBlockSignOut ? <ModalWorkingAntiLogout extraData={modalBlockSignOut} {...{ setModalBlockSignOut }} /> : null}
        </div>
    )
}

const useSignOutLogic = () => {
    const [modalBlockSignOut, setModalBlockSignOut] = useState<TwinDictionary | null>(null)

    const checkSignOut = useCallback(() => {
        const bottom = getBottomNavApp()
        if(bottom.workingModal){
            setModalBlockSignOut(bottom.workingModal.extraData)
        }else {
            signOutUser()
        }
    }, [setModalBlockSignOut])

    return { modalBlockSignOut, setModalBlockSignOut, checkSignOut }
}


export default UserIcons